<template>
  <div>
    <div class="d-flex mb-3">
      <h1 class="h4 primary--text">
        {{ $t('views.consolidator_asn_shipment.consolidator_asn_shipment') }}
      </h1>
    </div>
    <main class="bg-white pa-6">
      <div class="asn-shipment">
        <div class="input-block input-block_white">
          <label class="input-block__label">
            {{ $t('common.pallet') }}
          </label>
          <v-text-field
            v-model="serialNumber"
            :loading="loadingPallet"
            solo
            @keydown.enter="checkSerialNumber"
          />
        </div>
        <v-data-table
          :items="packing"
          :headers="headers"
          disable-pagination
          disable-sort
          hide-default-footer
        >
          <template v-slot:item.asn="{ value }">
            {{ `${value.number} ${$t('common.dated')} ${$moment(value.date).format('L')}` }}
          </template>
          <template v-slot:item.delete="{ item }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                @click="removePallet(item)"
              >
                mdi-delete
              </v-icon>
            </v-hover>
          </template>
        </v-data-table>
        <div class="mt-10">
          <v-btn
            :disabled="saving"
            color="primary"
            class="mr-5"
            @click="save"
          >
            {{ $t('common.save') }}
          </v-btn>
          <v-btn
            outlined
            :disabled="labelsProcessing"
            color="primary"
            class="mr-5"
            @click="getLabels"
          >
            {{ $t('views.consolidator_asn_shipment.get_labels') }}
          </v-btn>
          <v-btn
            v-if="isNew"
            outlined
            :disabled="saving"
            color="primary"
            @click="restart"
          >
            {{ $t('views.consolidator_asn_shipment.restart') }}
          </v-btn>
        </div>
      </div>
    </main>
    <pallet-modal
      v-model="isModalShown"
      :serial-number="serialNumber"
      :asn-list="asnList"
      @selectAsn="selectAsn"
    />
    <shipment-notice-errors-modal
      v-model="isErrorsModalShown"
      :error-list="errorsModalList"
      :title="$t('views.shipment_notice_edit.validation_errors')"
    />
  </div>
</template>

<script>
import PalletModal from '@/components/consolidator-asn/PalletModal.vue';
import ShipmentNoticeErrorsModal from '@/components/asn/ShipmentNoticeErrorsModal.vue';

export default {
  name: 'ConsolidatorAsnShipment',

  components: {
    PalletModal,
    ShipmentNoticeErrorsModal,
  },

  props: {
    isNew: {
      type: Boolean,
      default: () => false,
    },

    packing: {
      type: Array,
      required: true,
    },

    saving: {
      type: Boolean,
      default: () => false,
    },

    isErrorsModalShown: {
      type: Boolean,
      default: () => false,
    },

    errorsModalList: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      serialNumber: '',
      loadingPallet: false,
      asnList: [],

      headers: [
        {
          text: this.$t('common.pallet'),
          value: 'boxSerialNumberLevel1',
        },
        {
          text: 'ASN',
          value: 'asn',
        },
        {
          text: this.$t('common.delete'),
          value: 'delete',
        },
      ],

      labelsProcessing: false,
      isModalShown: false,
    };
  },

  methods: {
    async checkSerialNumber() {
      if (!this.serialNumber.startsWith('S') || this.serialNumber.length < 2) {
        return;
      }

      this.loadingPallet = true;
      this.asnList = [];
      try {
        const { data } = await this.$http.get(`/shipment/check-pallet/${this.serialNumber.slice(1)}`);
        if (data) {
          this.packing.push({
            boxSerialNumberLevel1: this.serialNumber.slice(1),
            asn: data,
          });
          this.serialNumber = '';
        }
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loadingPallet = false;
      }
    },

    selectAsn(asn) {
      this.packing.push({ boxSerialNumberLevel1: this.serialNumber.slice(1), asn });
      this.isModalShown = false;
      this.serialNumber = '';
    },

    removePallet(item) {
      const index = this.packing.findIndex(
        (position) => position.boxSerialNumberLevel1 === item.boxSerialNumberLevel1,
      );
      if (index !== -1) {
        this.packing.splice(index, 1);
      }
    },

    save() {
      this.$emit('save');
    },

    async getLabels() {
      this.labelsProcessing = true;
      try {
        const asnIds = this.packing.reduce((acc, position) => {
          if (!acc.includes(position.asn.id)) {
            acc.push(position.asn.id);
          }
          return acc;
        }, []);
        const promises = asnIds.map((asnId, index) => this.getLabel(asnId, index));
        await Promise.all(promises);
      } finally {
        this.labelsProcessing = false;
      }
    },

    async getLabel(asnId, index) {
      try {
        const { data } = await this.$http.get(`/labels/${asnId}`, {
          responseType: 'blob',
        });
        if (data) {
          const url = URL.createObjectURL(data);
          const anchor = document.createElement('a');
          anchor.style.display = 'none';
          anchor.download = `label-${index + 1}.pdf`;
          anchor.href = url;
          anchor.click();
        }
      } catch (err) {
        this.$http.mockApiError(err);
      }
    },

    restart() {
      this.$emit('restart');
    },
  },
};
</script>

<style lang="scss" scoped>
.asn-shipment {
  max-width: 1082px;
}
</style>
